import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import useBrandDetail from './hooks/useBrandDetail';
import BrandInfoCard from './components/BrandInfoCard';
import PillarCard from './components/PillarCard';
import CompetitorsCard from './components/CompetitorsCard';
import BrandSettingsCard from './components/BrandSettingsCard';
import EditBrandModal from './components/EditBrandModal';
import CollaboratorsModal from './components/CollaboratorsModal';
import { V } from './vividGlass';

const PILLAR_KEYS = ['core', 'market', 'voice', 'visual'];

export default function BrandDnaDetailScreen() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { brand, isLoading, refresh } = useBrandDetail(brandId);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);

  if (isLoading) {
    return (
      <div style={{ padding: 32 }}>
        <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{
            height: 160, borderRadius: 24,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
            marginBottom: 16, animation: 'pulse 1.5s ease-in-out infinite',
          }} />
        ))}
      </div>
    );
  }

  if (!brand) return null;

  return (
    <div style={{ minHeight: '100vh', background: V.bgGradient, fontFamily: V.font }}>
      <style>{`
        .detail-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }
        @media (max-width: 820px) {
          .detail-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Page content — max-w-7xl with px-8 per spec */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '32px 32px 60px' }}>

        {/* Inline back button — not fixed (app navbar is fixed at zIndex:1000/height:56) */}
        <motion.button
          initial={{ opacity: 0, x: -6 }}
          animate={{ opacity: 1, x: 0 }}
          whileHover={{ x: -2 }}
          whileTap={{ scale: 0.95 }}
          type="button"
          onClick={() => navigate('/brand-dna')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 7,
            padding: '8px 16px', borderRadius: 99, marginBottom: 20,
            background: 'rgba(255,255,255,0.06)',
            border: `1px solid ${V.glassBorder}`,
            color: V.textSub, fontSize: 13, fontWeight: 500,
            cursor: 'pointer', fontFamily: V.font,
          }}
        >
          <ArrowLeft size={14} /> Back to Brands
        </motion.button>

        {/* Main brand info card */}
        <BrandInfoCard
          brand={brand}
          brandId={brandId}
          onEditClick={() => setShowEditModal(true)}
          onCollaboratorsClick={() => setShowCollaboratorsModal(true)}
          onNavigate={navigate}
        />

        {/* 4 Pillar cards — 2-col grid */}
        <div className="detail-grid">
          {PILLAR_KEYS.map((key) => (
            <PillarCard
              key={key}
              pillarKey={key}
              brand={brand}
              brandId={brandId}
              onRefresh={refresh}
            />
          ))}
        </div>

        {/* Competitors */}
        <div style={{ marginTop: 24 }}>
          <CompetitorsCard brand={brand} brandId={brandId} onRefresh={refresh} />
        </div>

        {/* Brand Settings */}
        <BrandSettingsCard
          brand={brand}
          brandId={brandId}
          isOwner={brand.is_owner}
          onDeleted={() => navigate('/brand-dna')}
          onRefresh={refresh}
        />
      </div>

      {/* Modals */}
      <EditBrandModal
        brand={brand}
        brandId={brandId}
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSaved={refresh}
      />
      <CollaboratorsModal
        brandId={brandId}
        collaborators={brand.collaborators || []}
        isOwner={brand.is_owner}
        open={showCollaboratorsModal}
        onClose={() => setShowCollaboratorsModal(false)}
        onRefresh={refresh}
      />
    </div>
  );
}
